import { Inventory2Outlined } from '@mui/icons-material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { alpha, Box, Stack, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { DeliveryItem } from '../../../../services/goods-receiving/goods-receiving.types';
import { MODULE_ACCENT_COLORS } from '../../../../theme/module-colors';
import { formatDate } from '../../../../utils/date-formatter';
import { getUnitLabel } from '../../../../utils/units';

const accent = MODULE_ACCENT_COLORS.goodsReceiving.main;

interface LocalItemsSectionProps {
  items: DeliveryItem[];
}

export function LocalItemsSection({ items }: LocalItemsSectionProps) {
  const { t } = useTranslation('goodsReceiving');

  return (
    <Box component="section">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5, px: 0.5 }}>
        <Inventory2Outlined sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {t('stepItems')}
        </Typography>
        <Box
          sx={{
            minWidth: 22,
            height: 22,
            px: 0.75,
            borderRadius: 999,
            bgcolor: alpha(accent, 0.14),
            color: accent,
            fontSize: '0.72rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {items.length}
        </Box>
      </Stack>

      <Stack spacing={1.25}>
        {items.map((item) => (
          <DeliveryItemSummaryCard key={item.id} item={item} />
        ))}
      </Stack>
    </Box>
  );
}

function getPurchaseSummaryText(t: TFunction, item: DeliveryItem) {
  const count = item.pastPurchaseCount ?? 0;
  const lastDate = item.lastPurchaseDate ?? null;

  if (count === 0) {
    return { text: t('purchaseHistoryNew'), count };
  }
  if (lastDate) {
    return {
      text: `${t('purchaseHistoryTimes', { count })} · ${t('purchaseHistoryLast', { date: formatDate(lastDate) })}`,
      count,
    };
  }
  return { text: t('purchaseHistoryTimes', { count }), count };
}

interface DeliveryItemSummaryCardProps {
  item: DeliveryItem;
}

function DeliveryItemSummaryCard({ item }: DeliveryItemSummaryCardProps) {
  const { t } = useTranslation('goodsReceiving');
  const hasStats = item.pastPurchaseCount !== undefined;
  const summaryText = hasStats ? getPurchaseSummaryText(t, item).text : '';

  return (
    <Box
      component="article"
      sx={{
        p: 1.75,
        borderRadius: 2.5,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '@media (hover: hover)': {
          '&:hover': {
            borderColor: alpha(accent, 0.35),
            boxShadow: `0 2px 10px ${alpha(accent, 0.1)}`,
          },
        },
      }}
      aria-label={hasStats ? t('purchaseHistoryAria', { summary: summaryText }) : undefined}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'background.default',
            border: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: 'text.secondary',
          }}
        >
          <Inventory2Outlined fontSize="small" />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.3,
            }}
          >
            {item.productName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {item.quantity} {item.unit ? getUnitLabel(item.unit) : ''}
            {item.unitPrice != null && item.currency && (
              <Typography
                component="span"
                sx={{ ml: 1, fontWeight: 'medium', color: 'text.primary' }}
              >
                {item.unitPrice.toFixed(2).replace('.', ',')} {item.currency}/
                {getUnitLabel(item.unit)}
              </Typography>
            )}
          </Typography>
        </Box>

        {item.labelPhotos.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'text.secondary',
              flexShrink: 0,
            }}
          >
            <PhotoCameraIcon fontSize="small" />
            <Typography variant="caption">{item.labelPhotos.length}</Typography>
          </Box>
        )}
      </Box>

      {(item.batchNumber || item.expiryDate) && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75, ml: 7 }}>
          {item.batchNumber && t('localItemBatchPrefix', { number: item.batchNumber })}
          {item.batchNumber && item.expiryDate && ' · '}
          {item.expiryDate && t('localItemExpiryPrefix', { date: formatDate(item.expiryDate) })}
        </Typography>
      )}

      <PurchaseHistoryRow item={item} />
    </Box>
  );
}

function PurchaseHistoryRow({ item }: DeliveryItemSummaryCardProps) {
  const { t } = useTranslation('goodsReceiving');
  const hasStats = item.pastPurchaseCount !== undefined;

  if (!hasStats) {
    return (
      <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px dashed', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Box sx={{ width: 100, height: 16, borderRadius: 1, bgcolor: 'grey.200' }} />
        </Box>
      </Box>
    );
  }

  const { text, count } = getPurchaseSummaryText(t, item);

  return (
    <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px dashed', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
          <HistoryOutlinedIcon sx={{ fontSize: 14 }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            {t('purchaseHistoryLabel')}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            fontSize: '0.72rem',
            color: count === 0 ? 'text.secondary' : accent,
            px: 1,
            py: 0.25,
            borderRadius: 999,
            bgcolor: count === 0 ? 'grey.100' : alpha(accent, 0.14),
          }}
        >
          {text}
        </Typography>
      </Box>
    </Box>
  );
}
